/*
  Design templates ("designteman") - a per-workspace look, chosen by a tenant
  owner/admin and applied for every member of that workspace.

  How it works: tailwind.config.js resolves every colour, font and radius
  through a --ct-* custom property whose default lives in :root in
  src/index.css. Applying a template just means setting those same properties
  inline on <html>, which is why swapping one re-skins the entire app - AG Grid
  included - without a single component knowing about it.

  Adding a template:
    1. add the id to DesignTemplateId and DESIGN_TEMPLATES below (all tokens,
       no partials - an undefined --ct-* produces invalid CSS),
    2. add it to the CHECK constraint in a new migration (the unit test asserts
       the TS catalogue and the SQL list stay in sync),
    3. add the i18n name/description to both en.json and sv.json.

  Palettes and font pairings are seeded from the ui-ux-pro-max data set
  (.agents/skills/ui-ux-pro-max-skill-main/src/ui-ux-pro-max/data/colors.csv and
  typography.csv). That data only defines six roles, so the container ladders
  here are hand-derived from those seeds. Every template is contrast-checked in
  src/lib/__tests__/designTemplates.test.ts - do not add one without running it.
*/

import { logger } from '../utils/logger';

export type DesignTemplateId = 'forest' | 'nordic' | 'dune' | 'coast' | 'midnight';

export const DEFAULT_DESIGN_TEMPLATE: DesignTemplateId = 'forest';

/** Matches `campidag_fav_travel` in src/pages/Home.tsx. */
const STORAGE_KEY = 'campidag_design_template';

/** One entry per colour in tailwind.config.js. */
export type ThemeColorToken =
  | 'primary'
  | 'primary-container'
  | 'primary-fixed'
  | 'secondary'
  | 'secondary-container'
  | 'secondary-fixed'
  | 'tertiary'
  | 'tertiary-container'
  | 'on-primary'
  | 'on-secondary'
  | 'on-tertiary'
  | 'surface'
  | 'surface-container-lowest'
  | 'surface-container-low'
  | 'surface-container'
  | 'surface-container-high'
  | 'surface-container-highest'
  | 'surface-dim'
  | 'surface-bright'
  | 'on-surface'
  | 'on-surface-variant'
  | 'outline'
  | 'outline-variant';

export type RadiusToken = 'sm' | 'base' | 'md' | 'lg' | 'xl';

export interface DesignTemplate {
  id: DesignTemplateId;
  /** Drives color-scheme, so native form controls and scrollbars follow along. */
  dark: boolean;
  nameKey: string;
  descriptionKey: string;
  colors: Record<ThemeColorToken, string>;
  fonts: {
    /** Full CSS stack for body text - always ends in a system fallback. */
    sans: string;
    /** Full CSS stack for h1/h2 (the "display" face). */
    serif: string;
    /**
     * Lazily pulls in the @fontsource CSS for the faces this template needs.
     * Vite code-splits these, so a workspace only downloads its own fonts.
     * Empty for templates that use the bundled Inter/Lora.
     */
    load: () => Promise<unknown>[];
  };
  radii: Record<RadiusToken, string>;
}

/**
 * Hex values for the Settings preview swatch. Derived rather than stored so it
 * can never drift out of sync with the palette it is previewing.
 */
export function templateSwatch(template: DesignTemplate): [string, string, string] {
  return [
    template.colors.primary,
    template.colors.tertiary,
    template.colors['surface-container-high'],
  ];
}

const RADII_DEFAULT: Record<RadiusToken, string> = {
  sm: '0.25rem',
  base: '0.5rem',
  md: '0.75rem',
  lg: '1rem',
  xl: '1.5rem',
};

/** Crisper, more architectural feel. */
const RADII_SHARP: Record<RadiusToken, string> = {
  sm: '0.125rem',
  base: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
};

/** Softer, rounder feel. */
const RADII_SOFT: Record<RadiusToken, string> = {
  sm: '0.375rem',
  base: '0.75rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
};

const STACK_SYSTEM_SANS = 'ui-sans-serif, system-ui, sans-serif';
const STACK_SYSTEM_SERIF = 'Georgia, serif';

export const DESIGN_TEMPLATES: Record<DesignTemplateId, DesignTemplate> = {
  /*
    The original Stitch "CampIdag" palette. These values are duplicated in the
    :root block of src/index.css on purpose - that block is what renders before
    any JS runs - and a snapshot test keeps the two in lockstep. Changing one
    without the other is a bug.
  */
  forest: {
    id: 'forest',
    dark: false,
    nameKey: 'settings.designTemplates.forest.name',
    descriptionKey: 'settings.designTemplates.forest.description',
    colors: {
      'primary': '#2e5940',
      'primary-container': '#3c6e4f',
      'primary-fixed': '#dce8dd',
      'secondary': '#4f7a5c',
      'secondary-container': '#a7d2b4',
      'secondary-fixed': '#d5e2d8',
      'tertiary': '#8a6d3f',
      'tertiary-container': '#ccb89b',
      'on-primary': '#ffffff',
      'on-secondary': '#ffffff',
      'on-tertiary': '#ffffff',
      'surface': '#faf7f0',
      'surface-container-lowest': '#ffffff',
      'surface-container-low': '#f5f0e5',
      'surface-container': '#efe9dc',
      'surface-container-high': '#e9e5d9',
      'surface-container-highest': '#e0d9c7',
      'surface-dim': '#e5ddcb',
      'surface-bright': '#fdfbf6',
      'on-surface': '#212b20',
      'on-surface-variant': '#545f4e',
      'outline': '#7d846f',
      'outline-variant': '#ded7c5',
    },
    fonts: {
      sans: `Inter, ${STACK_SYSTEM_SANS}`,
      serif: `Lora, ${STACK_SYSTEM_SERIF}`,
      load: () => [],
    },
    radii: RADII_DEFAULT,
  },

  /* Seed: ui-ux-pro-max colors.csv row 39 (Travel/Tourism), cooled down. */
  nordic: {
    id: 'nordic',
    dark: false,
    nameKey: 'settings.designTemplates.nordic.name',
    descriptionKey: 'settings.designTemplates.nordic.description',
    colors: {
      'primary': '#15597f',
      'primary-container': '#1c6d99',
      'primary-fixed': '#d6e9f5',
      'secondary': '#356e8f',
      'secondary-container': '#a9cfe3',
      'secondary-fixed': '#d7e6ef',
      'tertiary': '#6b6f7b',
      'tertiary-container': '#c7cbd4',
      'on-primary': '#ffffff',
      'on-secondary': '#ffffff',
      'on-tertiary': '#ffffff',
      'surface': '#f7fafc',
      'surface-container-lowest': '#ffffff',
      'surface-container-low': '#eef4f8',
      'surface-container': '#e6eef4',
      'surface-container-high': '#dee8f0',
      'surface-container-highest': '#d3e0ea',
      'surface-dim': '#dbe4ec',
      'surface-bright': '#fbfdfe',
      'on-surface': '#152029',
      'on-surface-variant': '#4a5a67',
      'outline': '#7b8b98',
      'outline-variant': '#cfdce5',
    },
    fonts: {
      sans: `'Open Sans', ${STACK_SYSTEM_SANS}`,
      serif: `Poppins, ${STACK_SYSTEM_SANS}`,
      load: () => [
        import('@fontsource/open-sans/400.css'),
        import('@fontsource/open-sans/500.css'),
        import('@fontsource/open-sans/600.css'),
        import('@fontsource/open-sans/700.css'),
        import('@fontsource/poppins/500.css'),
        import('@fontsource/poppins/600.css'),
        import('@fontsource/poppins/700.css'),
      ],
    },
    radii: RADII_SHARP,
  },

  /* Warm sand and terracotta - the tan end of the original palette, pushed. */
  dune: {
    id: 'dune',
    dark: false,
    nameKey: 'settings.designTemplates.dune.name',
    descriptionKey: 'settings.designTemplates.dune.description',
    colors: {
      'primary': '#8a4f2b',
      'primary-container': '#a35f34',
      'primary-fixed': '#f5e3d3',
      'secondary': '#7a6142',
      'secondary-container': '#d9c3a5',
      'secondary-fixed': '#efe2d2',
      'tertiary': '#616b3f',
      'tertiary-container': '#cfd4b4',
      'on-primary': '#ffffff',
      'on-secondary': '#ffffff',
      'on-tertiary': '#ffffff',
      'surface': '#fbf6ef',
      'surface-container-lowest': '#ffffff',
      'surface-container-low': '#f6efe4',
      'surface-container': '#f0e7d9',
      'surface-container-high': '#e9dfcf',
      'surface-container-highest': '#e0d3c0',
      'surface-dim': '#e5d8c6',
      'surface-bright': '#fdfaf5',
      'on-surface': '#2b2318',
      'on-surface-variant': '#5d5342',
      'outline': '#8b7f6b',
      'outline-variant': '#e0d5c3',
    },
    fonts: {
      sans: `Inter, ${STACK_SYSTEM_SANS}`,
      serif: `'Playfair Display', ${STACK_SYSTEM_SERIF}`,
      load: () => [
        import('@fontsource/playfair-display/500.css'),
        import('@fontsource/playfair-display/600.css'),
        import('@fontsource/playfair-display/700.css'),
      ],
    },
    radii: RADII_SOFT,
  },

  /* Seed: colors.csv row 22 (Sustainability/ESG), shifted from green to teal. */
  coast: {
    id: 'coast',
    dark: false,
    nameKey: 'settings.designTemplates.coast.name',
    descriptionKey: 'settings.designTemplates.coast.description',
    colors: {
      'primary': '#0f6b66',
      'primary-container': '#12837c',
      'primary-fixed': '#cdeae7',
      'secondary': '#3a6f7a',
      'secondary-container': '#a9d2d4',
      'secondary-fixed': '#d3e6e8',
      'tertiary': '#a05c3c',
      'tertiary-container': '#e3c3ad',
      'on-primary': '#ffffff',
      'on-secondary': '#ffffff',
      'on-tertiary': '#ffffff',
      'surface': '#f6faf9',
      'surface-container-lowest': '#ffffff',
      'surface-container-low': '#ecf4f3',
      'surface-container': '#e2eeec',
      'surface-container-high': '#d8e6e4',
      'surface-container-highest': '#cbdcda',
      'surface-dim': '#d9e6e4',
      'surface-bright': '#fbfdfd',
      'on-surface': '#122423',
      'on-surface-variant': '#45585a',
      'outline': '#7a8f8f',
      'outline-variant': '#cfe0de',
    },
    fonts: {
      sans: `Raleway, ${STACK_SYSTEM_SANS}`,
      serif: `Lora, ${STACK_SYSTEM_SERIF}`,
      load: () => [
        import('@fontsource/raleway/400.css'),
        import('@fontsource/raleway/500.css'),
        import('@fontsource/raleway/600.css'),
        import('@fontsource/raleway/700.css'),
      ],
    },
    radii: RADII_DEFAULT,
  },

  /*
    The dark template. Note the inversion: `primary` is LIGHT here so
    `text-primary` stays readable on a dark surface, and `on-primary` carries
    the dark foreground for `bg-primary` buttons. That is exactly why the
    on-* tokens exist - see the comment in tailwind.config.js.
  */
  midnight: {
    id: 'midnight',
    dark: true,
    nameKey: 'settings.designTemplates.midnight.name',
    descriptionKey: 'settings.designTemplates.midnight.description',
    colors: {
      'primary': '#8fd6a5',
      'primary-container': '#6fbd87',
      'primary-fixed': '#2c4636',
      'secondary': '#a8ccb2',
      'secondary-container': '#7fae8e',
      'secondary-fixed': '#2a3d31',
      'tertiary': '#dcb98a',
      'tertiary-container': '#4a3a22',
      'on-primary': '#0b2916',
      'on-secondary': '#12301d',
      'on-tertiary': '#33240f',
      'surface': '#151a17',
      'surface-container-lowest': '#0f1310',
      'surface-container-low': '#1a201c',
      'surface-container': '#202722',
      'surface-container-high': '#29312b',
      'surface-container-highest': '#333c35',
      'surface-dim': '#0d100e',
      'surface-bright': '#29312b',
      'on-surface': '#e7ede7',
      'on-surface-variant': '#b2bcb3',
      'outline': '#7e8a80',
      'outline-variant': '#3a423c',
    },
    fonts: {
      sans: `Inter, ${STACK_SYSTEM_SANS}`,
      serif: `'Space Grotesk', ${STACK_SYSTEM_SANS}`,
      load: () => [
        import('@fontsource/space-grotesk/500.css'),
        import('@fontsource/space-grotesk/600.css'),
        import('@fontsource/space-grotesk/700.css'),
      ],
    },
    radii: RADII_DEFAULT,
  },
};

export const DESIGN_TEMPLATE_IDS = Object.keys(DESIGN_TEMPLATES) as DesignTemplateId[];

/** Anything unknown (stale cache, a value written by a newer client) → default. */
export function resolveTemplateId(value: string | null | undefined): DesignTemplateId {
  // hasOwnProperty, not `in`: `in` walks the prototype chain, so 'toString' and
  // 'constructor' would pass as valid ids and hand back a Function.
  // (Object.hasOwn would read better but needs an es2022 lib target.)
  return value && Object.prototype.hasOwnProperty.call(DESIGN_TEMPLATES, value)
    ? (value as DesignTemplateId)
    : DEFAULT_DESIGN_TEMPLATE;
}

export function getDesignTemplate(value: string | null | undefined): DesignTemplate {
  return DESIGN_TEMPLATES[resolveTemplateId(value)];
}

const CSS_VAR_NAMES: string[] = [
  ...(Object.keys(DESIGN_TEMPLATES.forest.colors) as ThemeColorToken[]).map((t) => `--ct-${t}`),
  '--ct-font-sans',
  '--ct-font-serif',
  '--ct-radius-sm',
  '--ct-radius',
  '--ct-radius-md',
  '--ct-radius-lg',
  '--ct-radius-xl',
];

function radiusVarName(token: RadiusToken): string {
  return token === 'base' ? '--ct-radius' : `--ct-radius-${token}`;
}

/**
 * Writes the template's tokens as inline custom properties on <html>.
 *
 * For the default template it *removes* them instead, so the :root block in
 * index.css takes over. That keeps 'forest' a literal no-op rather than a
 * re-statement of the same values, and means a workspace that never picks a
 * template renders byte-identically to before this feature existed.
 */
export function applyDesignTemplate(
  id: DesignTemplateId,
  root: HTMLElement = document.documentElement
): void {
  const template = DESIGN_TEMPLATES[resolveTemplateId(id)];

  if (template.id === DEFAULT_DESIGN_TEMPLATE) {
    for (const name of CSS_VAR_NAMES) root.style.removeProperty(name);
    delete root.dataset.designTemplate;
    root.style.removeProperty('color-scheme');
    return;
  }

  for (const [token, value] of Object.entries(template.colors)) {
    root.style.setProperty(`--ct-${token}`, value);
  }
  root.style.setProperty('--ct-font-sans', template.fonts.sans);
  root.style.setProperty('--ct-font-serif', template.fonts.serif);
  for (const [token, value] of Object.entries(template.radii)) {
    root.style.setProperty(radiusVarName(token as RadiusToken), value);
  }

  root.dataset.designTemplate = template.id;
  root.style.setProperty('color-scheme', template.dark ? 'dark' : 'light');

  // Fire-and-forget: --ct-font-* always ends in a system stack, so text paints
  // immediately and swaps when the webfont lands (no FOIT, no await needed).
  for (const pending of template.fonts.load()) {
    pending.catch((err) => logger.error('Failed to load design template font', err));
  }
}

export function readCachedTemplateId(): DesignTemplateId {
  try {
    return resolveTemplateId(localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private mode / disabled storage - not worth logging on every boot.
    return DEFAULT_DESIGN_TEMPLATE;
  }
}

export function cacheTemplateId(id: DesignTemplateId): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

export function clearCachedTemplate(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Which routes wear the workspace's template.
 *
 * Only the signed-in app does. The public pages have no workspace context yet,
 * and /admin is the platform console - a platform admin who also belongs to a
 * workspace should still see the standard chrome there, so the console stays
 * recognisable. Kept here so main.tsx (pre-paint) and DesignTemplateLoader
 * (post-auth) can never disagree about it.
 */
const UNTHEMED_PREFIXES = [
  '/admin',
  '/welcome',
  '/login',
  '/apply',
  '/privacy',
  '/terms',
  '/contact',
  '/set-password',
  '/join-travel',
];

export function isThemedPath(pathname: string): boolean {
  return !UNTHEMED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
